import React, { useState, useRef, useEffect } from "react";
import { MultiStep, StepProps } from '@org/ui';
import { JourneyForm, JourneyFormRef } from '../forms/journey';
import { Table } from '@org/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { trpc, RouterOutputs } from '@org/clients/school';
import { toast } from 'sonner';
import { UnitSelect } from '@org/model';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@org/ui';
import { Button } from '@org/ui';
import { Separator } from '@org/ui';
import { Skeleton } from '@org/ui';
import { unwrapTrpcData, cn } from '@org/ui';
import { useDndSensors } from '../shared/use-dnd-sensors';
import { getMutationErrorMessage, hasOrderChanged } from '../shared/multi-step-utils';
import { SortableItemsCard } from '../shared/sortable-items-card';
import { resolveAfterSave, useStepNavigation } from '../shared/step-navigation';
import { UnsavedChangesDialog } from '../shared/unsaved-changes-dialog';
import { useTranslation } from 'react-i18next';

type getUnitsForJourneyType = RouterOutputs['journeys']['getUnitsForJourney'][number];

type JourneyMultiStepFormProps = {
  defaultValues: any;
  onSubmit: () => void;
  onClose: () => void;
  isEditing: boolean;
};

export const JourneyMultiStepForm: React.FC<JourneyMultiStepFormProps> = ({
  defaultValues,
  onSubmit,
  onClose,
  isEditing
}) => {
  const { t } = useTranslation('admin');

  const steps: StepProps[] = [
    { title: t('journeys.form.steps.journeyInfo') },
    { title: t('journeys.form.steps.units') },
    { title: t('journeys.form.steps.summary') }
  ];

  const states = ["journey", "units", "summary"];
  // Creating = wizard (steps unlock as they are saved); editing = free navigation.
  const nav = useStepNavigation(steps.length, isEditing);

  const [journeyData, setJourneyData] = useState<Record<string, any>>(defaultValues || {});
  const [selectedUnits, setSelectedUnits] = useState<getUnitsForJourneyType[]>([]);
  // What the server holds for the units step (loaded, then refreshed after each save).
  const [savedUnits, setSavedUnits] = useState<getUnitsForJourneyType[]>([]);
  // Unsaved edits on the journey step (react-hook-form `isDirty`); `formKey` remounts it to discard them.
  const [stepDirty, setStepDirty] = useState(false);
  const [formKey, setFormKey] = useState(0);
  // Where the admin wanted to go when the unsaved-changes dialog opened.
  const [pendingTarget, setPendingTarget] = useState<number | null>(null);
  const [unsavedOpen, setUnsavedOpen] = useState(false);

  const sensors = useDndSensors();

  const formRef = useRef<JourneyFormRef>(null);

  const { data: unitsData } = trpc.units.list.useQuery();
  const [unitData, setUnitData] = useState<UnitSelect[]>([]);

  const journeyId = defaultValues?.journeyId;
  const { data: journeyUnits, isLoading: isLoadingJourneyUnits } =
    trpc.journeys.getUnitsForJourney.useQuery(journeyId, {
      enabled: isEditing && !!journeyId
    });

  const saveUnitsMutation = trpc.journeys.saveUnitsForJourney.useMutation({
    onSuccess: () => toast.success(t('journeys.form.toasts.unitsSaved')),
    onError: (error: unknown) => toast.error(t('journeys.form.toasts.unitsSaveFailed', { message: getMutationErrorMessage(error) })),
  });

  const updateUnitOrderMutation = trpc.journeys.updateUnitOrder.useMutation({
    onSuccess: () => toast.success(t('journeys.form.toasts.unitOrderSaved')),
    onError: (error: unknown) => toast.error(t('journeys.form.toasts.unitOrderFailed', { message: getMutationErrorMessage(error) })),
  });

  useEffect(() => {
    const unwrapped = unwrapTrpcData<UnitSelect[]>(unitsData as UnitSelect[]);
    if (unwrapped) {
      setUnitData(unwrapped);
    }
  }, [unitsData]);

  useEffect(() => {
    const unwrapped = unwrapTrpcData<getUnitsForJourneyType[]>(journeyUnits);
    if (unwrapped && unwrapped.length > 0) {
      const sortedUnits = [...unwrapped].sort((a, b) => {
        const orderA = a.order ?? 0;
        const orderB = b.order ?? 0;
        return orderA - orderB;
      });

      setSelectedUnits(sortedUnits);
      setSavedUnits(sortedUnits);
    }
  }, [journeyUnits]);

  /** Units step differs from what was last saved (selection or order). */
  const unitsUnsaved = (): boolean =>
    hasOrderChanged(selectedUnits, savedUnits, (u) => u.unitId, (u, i) => u.order ?? i);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setSelectedUnits((items) => {
        const oldIndex = items.findIndex(item => item.unitId === active.id);
        const newIndex = items.findIndex(item => item.unitId === over.id);

        const reorderedItems = arrayMove(items, oldIndex, newIndex).map((unitItem, index) => ({
          ...unitItem,
          order: index,
        }));

        return reorderedItems;
      });
    }
  };

  /** Save the units step when it has unsaved edits, then `onDone` (also when there was nothing to save). */
  const saveUnits = (onDone?: () => void) => {
    if (!journeyData.journeyId || !unitsUnsaved()) {
      onDone?.();
      return;
    }
    const units = selectedUnits;
    saveUnitsMutation.mutate(
      { journeyId: journeyData.journeyId, units: units.map((u, index) => ({ unitId: u.unitId, order: u.order ?? index })) },
      { onSuccess: () => { setSavedUnits(units); onDone?.(); } },
    );
  };

  const stepHasUnsavedEdits = (index: number): boolean =>
    index === 0 ? stepDirty : index === 1 ? unitsUnsaved() : false;

  /** Move; in the wizard the next step unlocks only through `next()`. */
  const moveTo = (target: number) => {
    if (nav.mode === 'wizard' && target === nav.currentIndex + 1) nav.next();
    else nav.goTo(target);
  };

  /** Header / Back click: honour the wizard lock, and never drop unsaved edits silently. */
  const requestStep = (target: number) => {
    if (target === nav.currentIndex || !nav.canReach(target)) return;
    if (stepHasUnsavedEdits(nav.currentIndex)) {
      setPendingTarget(target);
      setUnsavedOpen(true);
      return;
    }
    moveTo(target);
  };

  const handleNext = () => {
    if (nav.currentIndex === 0) {
      setPendingTarget(null);
      formRef.current?.submit(); // → saveJourney moves on
      return;
    }
    if (nav.currentIndex === 1) {
      saveUnits(() => nav.next());
      return;
    }
    nav.next();
  };

  const saveJourney = (data: Record<string, any>) => {
    setJourneyData({ ...journeyData, ...data });
    setStepDirty(false);
    const target = resolveAfterSave(pendingTarget, nav.currentIndex, steps.length);
    setPendingTarget(null);
    setUnsavedOpen(false);
    moveTo(target);
  };

  const handleUnsavedSave = () => {
    if (nav.currentIndex === 0) {
      formRef.current?.submit(); // → saveJourney closes the dialog and moves
      return;
    }
    const target = pendingTarget;
    saveUnits(() => {
      setUnsavedOpen(false);
      setPendingTarget(null);
      if (target !== null) moveTo(target);
    });
  };

  const handleUnsavedDiscard = () => {
    const target = pendingTarget;
    if (nav.currentIndex === 0) {
      setFormKey((k) => k + 1);
      setStepDirty(false);
    } else if (nav.currentIndex === 1) {
      setSelectedUnits(savedUnits);
    }
    setUnsavedOpen(false);
    setPendingTarget(null);
    if (target !== null) moveTo(target);
  };

  const handleUnsavedCancel = () => {
    setUnsavedOpen(false);
    setPendingTarget(null);
  };

  const toggleUnitSelection = (unitItem: UnitSelect) => {
    setSelectedUnits(prevSelected => {
      const isSelected = prevSelected.some(item => item.unitId === unitItem.unitId);
      if (isSelected) {
        return prevSelected.filter(item => item.unitId !== unitItem.unitId);
      } else {
        const nextOrder = prevSelected.length;
        return [...prevSelected, {
          ...unitItem,
          order: nextOrder,
        }];
      }
    });
  };

  const handleFinalSubmit = () => saveUnits(() => onSubmit());

  const columnHelper = createColumnHelper<UnitSelect>();

  const unitColumns = [
    columnHelper.accessor((row) => row.name || 'Unnamed unit', {
      id: 'name',
      header: t('journeys.form.units.columnName'),
    }),
    columnHelper.accessor((row) => row.description || '', {
      id: 'description',
      header: t('journeys.form.units.columnDescription'),
    }),
    columnHelper.display({
      id: 'select',
      header: t('journeys.form.units.columnSelect'),
      cell: (info) => {
        const row = info.row.original;
        const isSelected = selectedUnits.some(item => item.unitId === row.unitId);
        return (
          <Button
            variant="outline"
            size="sm"
            aria-pressed={isSelected}
            className={cn(isSelected && 'ring-2 ring-primary')}
            onClick={() => toggleUnitSelection(row)}
          >
            {isSelected ? t('journeys.form.units.selected') : t('journeys.form.units.select')}
          </Button>
        );
      },
    }),
  ];

  const handleUnitRemoval = (item: UnitSelect | getUnitsForJourneyType) => {
    const unitToRemove = selectedUnits.find(u => u.unitId === item.unitId);
    if (unitToRemove) {
      toggleUnitSelection(unitToRemove as any);
    }
  };


  return (
    <div className="flex flex-col w-full min-h-screen bg-muted/30">
      <div className="w-full px-8 py-4 bg-background border-b shadow-sm">
        <MultiStep
          steps={steps}
          currentStep={nav.currentIndex}
          onChange={requestStep}
          mode={nav.mode}
          reachable={nav.reachable}
          labels={{ completed: t('common.steps.completed'), current: t('common.steps.current'), pending: t('common.steps.pending') }}
        />
      </div>

      <div className="w-full flex-grow px-6 pt-6 pb-28 sm:pb-6 max-w-5xl mx-auto mt-6">
        {states[nav.currentIndex] === "journey" && (
          <Card>
            <CardHeader>
              <CardTitle>{t('journeys.form.journeyInfo.title')}</CardTitle>
              <CardDescription>{t('journeys.form.journeyInfo.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <JourneyForm
                key={formKey}
                defaultValues={journeyData}
                onSubmit={saveJourney}
                onClose={onClose}
                hideButtons={true}
                onDirtyChange={setStepDirty}
                ref={formRef}
              />
            </CardContent>
          </Card>
        )}
        {states[nav.currentIndex] === "units" && (
          <Card>
            <CardHeader>
              <CardTitle>{t('journeys.form.units.title')}</CardTitle>
              <CardDescription>{t('journeys.form.units.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingJourneyUnits && isEditing ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                    {t('journeys.form.units.loading')}
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <SortableItemsCard
                    items={selectedUnits}
                    sensors={sensors}
                    onDragEnd={handleDragEnd}
                    onRemove={handleUnitRemoval}
                    idKey="unitId"
                    nameKey="name"
                    descriptionKey="description"
                    title={t('journeys.form.units.selectedTitle')}
                    emptyTitle={t('journeys.form.units.emptyTitle')}
                    emptyDescription={t('journeys.form.units.emptyDescription')}
                  />

                  <div className="mt-8">
                    <h3 className="font-medium mb-3 text-foreground border-b pb-2">{t('journeys.form.units.availableTitle')}</h3>
                    <Table
                      tableData={unitData || []}
                      columns={unitColumns}
                      getRowId={(row: any) => row.unitId}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {states[nav.currentIndex] === "summary" && (
          <Card>
            <CardHeader>
              <CardTitle>{t('journeys.form.summary.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Card className="bg-muted/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium">{t('journeys.form.summary.journeyInfoTitle')}</CardTitle>
                  <Separator />
                </CardHeader>
                <CardContent className="space-y-2">
                  <p><span className="font-medium text-muted-foreground">{t('journeys.form.summary.nameLabel')}</span> {journeyData.name}</p>
                  <p><span className="font-medium text-muted-foreground">{t('journeys.form.summary.descriptionLabel')}</span> {journeyData.description || t('journeys.form.summary.none')}</p>
                </CardContent>
              </Card>

              {selectedUnits.length > 0 && (
                <Card className="bg-muted/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-medium">{t('journeys.form.summary.unitsTitle', { count: selectedUnits.length })}</CardTitle>
                    <Separator />
                  </CardHeader>
                  <CardContent>
                    <ol className="list-decimal pl-6 space-y-2">
                      {selectedUnits.map(unitItem => (
                        <li key={unitItem.unitId} className="text-muted-foreground">
                          <span className="font-medium">{unitItem.name}</span>
                          {unitItem.description && (
                            <p className="text-sm text-muted-foreground/70 mt-0.5">{unitItem.description}</p>
                          )}
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <div className="mobile-sticky-action-bar w-full py-4 px-6 flex justify-between bg-background border-t shadow-sm sm:mt-auto">
        {nav.currentIndex > 0 && (
          <Button
            variant="outline"
            onClick={() => requestStep(nav.currentIndex - 1)}
          >
            {t('journeys.form.actions.back')}
          </Button>
        )}
        <div className="ml-auto">
          {!nav.isLast ? (
            <Button
              variant="outline"
              onClick={handleNext}
            >
              {t('journeys.form.actions.next')}
            </Button>
          ) : (
            <Button
              onClick={handleFinalSubmit}
              variant="outline"
            >
              {t('journeys.form.actions.saveFinish')}
            </Button>
          )}
        </div>
      </div>

      <UnsavedChangesDialog
        open={unsavedOpen}
        onSave={handleUnsavedSave}
        onDiscard={handleUnsavedDiscard}
        onCancel={handleUnsavedCancel}
        saving={saveUnitsMutation.isPending}
      />
    </div>
  );
};
